import { type ChangeEvent, type DragEvent, useRef, useState } from 'react';

import {
  type PartAttachment,
  useAttachments,
  useDeleteAttachment,
  useUploadAttachment,
} from '@/lib/attachments';
import { cn } from '@/lib/cn';

interface AttachmentsSectionProps {
  orgSlug: string;
  partId: number;
}

const ACCEPT = 'image/jpeg,image/png,image/gif,image/webp';

export function AttachmentsSection({ orgSlug, partId }: AttachmentsSectionProps) {
  const list = useAttachments(orgSlug, partId, 'image');
  const upload = useUploadAttachment(orgSlug, partId, 'image');
  const remove = useDeleteAttachment(orgSlug, partId);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const [dragOver, setDragOver] = useState(false);
  // Default-hide the upload affordance behind a compact "+ Upload" so
  // the drawer doesn't burn a row of vertical space on a feature most
  // users only need occasionally. Click expands the drag-and-drop
  // block; cancelable.
  const [uploadOpen, setUploadOpen] = useState(false);

  const onFiles = async (files: FileList | null) => {
    if (!files || files.length === 0) return;
    // Sequential rather than parallel so the server's MAX_PER_PART check
    // sees a stable count between uploads. Also keeps the optimistic-
    // count UX simple: each upload's success refetches the list.
    for (const file of Array.from(files)) {
      try {
        await upload.mutateAsync(file);
      } catch {
        // Surface only the latest error in the form below; we still
        // attempt the rest so a single oversize file doesn't abort
        // the user's full batch.
      }
    }
  };

  const onSelect = (event: ChangeEvent<HTMLInputElement>) => {
    onFiles(event.target.files);
    event.target.value = ''; // allow re-uploading the same file later
  };

  const onDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragOver(false);
    onFiles(event.dataTransfer.files);
  };

  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">
          Images
        </h3>
        {!uploadOpen && (
          <button
            type="button"
            onClick={() => setUploadOpen(true)}
            className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
          >
            + Upload
          </button>
        )}
      </div>

      {uploadOpen && (
        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
          className={cn(
            'mb-2 flex items-center justify-center gap-2 rounded-md border dashed px-4 py-3 text-xs transition-colors',
            dragOver
              ? 'border border-forge-400 bg-forge-50 dark:bg-stone-800 text-forge-700 dark:text-forge-400'
              : 'border border-stone-300 dark:border-stone-700 bg-stone-50 dark:bg-stone-900 text-stone-500 dark:text-stone-400',
          )}
        >
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={upload.isPending}
            className="text-forge-700 dark:text-forge-400 hover:underline"
          >
            {upload.isPending ? 'Uploading…' : 'Choose a file'}
          </button>
          <span>or drop one here. JPG/PNG/GIF/WebP, up to 5 MB.</span>
          <button
            type="button"
            onClick={() => setUploadOpen(false)}
            className="ml-auto text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200"
            aria-label="Cancel upload"
          >
            ✕
          </button>
          <input
            ref={inputRef}
            type="file"
            accept={ACCEPT}
            multiple
            onChange={onSelect}
            className="hidden"
          />
        </div>
      )}

      {upload.isError && (
        <p className="mb-2 text-xs text-red-600">
          {(upload.error as Error).message}
        </p>
      )}

      {list.data && list.data.length > 0 ? (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {list.data.map((att) => (
            <Thumbnail
              key={att.id}
              attachment={att}
              onDelete={() => {
                if (confirm(`Delete ${att.filename}?`)) {
                  remove.mutate(att.id);
                }
              }}
              deleting={remove.isPending}
            />
          ))}
        </div>
      ) : (
        <p className="text-xs text-stone-500 dark:text-stone-400">No images yet.</p>
      )}

      {remove.isError && (
        <p className="mt-2 text-xs text-red-600">
          {(remove.error as Error).message}
        </p>
      )}
    </section>
  );
}

function Thumbnail({
  attachment,
  onDelete,
  deleting,
}: {
  attachment: PartAttachment;
  onDelete: () => void;
  deleting: boolean;
}) {
  return (
    <div className="group relative">
      <a
        href={attachment.file_url}
        target="_blank"
        rel="noopener noreferrer"
        className="block aspect-square overflow-hidden rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900"
        title={`${attachment.filename} (${prettyBytes(attachment.size_bytes)})`}
      >
        <img
          src={attachment.file_url}
          alt={attachment.filename}
          className="h-full w-full object-cover"
          loading="lazy"
        />
      </a>
      <button
        type="button"
        onClick={onDelete}
        disabled={deleting}
        className="absolute right-1 top-1 hidden rounded-full bg-white/90 px-1.5 py-0.5 text-xs text-red-600 shadow group-hover:block dark:bg-stone-900/90"
        aria-label="Delete image"
      >
        ✕
      </button>
    </div>
  );
}

function prettyBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}
